import { buildProps } from '@zoryn/utils'

import type { ExtractPublicPropTypes, PropType } from 'vue'
import type { HttpRequest } from '@zoryn/network'
import type { TaskActionLog, TaskRecord } from './types'
import { DEFAULT_TASK_API_PREFIX } from './constants'

export const taskCenterProps = buildProps({
  /**
   * @description HTTP client for remote task APIs
   */
  http: {
    type: Object as PropType<HttpRequest>,
  },
  /**
   * @description Task API prefix, default `/system/task`
   */
  apiPrefix: {
    type: String,
    default: DEFAULT_TASK_API_PREFIX,
  },
  /**
   * @description Whether to load tasks on mount
   */
  autoLoad: {
    type: Boolean,
    default: true,
  },
  /**
   * @description Use mock data when unauthenticated or remote request fails
   */
  fallbackToMock: {
    type: Boolean,
    default: true,
  },
  /**
   * @description Whether the user is authenticated; when false, skip remote and use mock
   */
  authenticated: {
    type: Boolean,
    default: undefined,
  },
  /**
   * @description Custom mock tasks for demo or fallback
   */
  mockTasks: {
    type: Array as PropType<TaskRecord[]>,
    default: undefined,
  },
  /**
   * @description Custom mock action logs keyed by task id
   */
  mockActionLogs: {
    type: Object as PropType<Record<string, TaskActionLog[]>>,
    default: undefined,
  },
  /**
   * @description Business route labels for open-business hint
   */
  bizTabLabels: {
    type: Object as PropType<Record<string, string>>,
    default: () => ({}),
  },
  /**
   * @description Section description shown above the task center
   */
  description: {
    type: String,
    default: '',
  },
} as const)

export type TaskCenterProps = ExtractPublicPropTypes<typeof taskCenterProps>

export const taskCenterEmits = {
  'open-business': (payload: { tab: string; task: TaskRecord }) =>
    Boolean(payload),
  load: (tasks: TaskRecord[]) => Array.isArray(tasks),
  error: (error: Error) => error instanceof Error,
  'task-start': (task: TaskRecord) => Boolean(task),
  'task-complete': (task: TaskRecord) => Boolean(task),
  'task-ignore': (task: TaskRecord) => Boolean(task),
}

export type TaskCenterEmits = typeof taskCenterEmits
